import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useEncryptedParams } from "../../hooks/useEncryptedParams";
import apiClient from "../../services/apiClient";
import expenseCategoryService from "../../services/expenseCategoryService";
import { normalizeApiError } from "../../utils/apiError";
import Button from "../../components/common/Button";
import SearchableDropdown from "../../components/common/SearchableDropdown";
import Input from "../../components/common/Input";
import TextArea from "../../components/common/TextArea";
import ErrorAlert from "../../components/common/ErrorAlert";
import alertService from "../../utils/alertService";
import { useMasterModals } from "../../hooks/useMasterModals";

const FilePreviewField = ({ label, value, onChange, error }) => {
  const isFileObj = value instanceof File;
  const hasValue = Boolean(value);

  let isImage = false;
  let isPdf = false;
  let previewUrl = "";

  if (isFileObj) {
    previewUrl = URL.createObjectURL(value);
    isImage = value.type.startsWith("image/");
    isPdf = value.type === "application/pdf";
  } else if (typeof value === "string") {
    previewUrl = value;
    const lowerUrl = value.toLowerCase();
    isImage =
      lowerUrl.endsWith(".png") ||
      lowerUrl.endsWith(".jpg") ||
      lowerUrl.endsWith(".jpeg") ||
      lowerUrl.endsWith(".webp") ||
      lowerUrl.endsWith(".gif");
    isPdf = lowerUrl.endsWith(".pdf");
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-3">
        {hasValue ? (
          <div className="flex items-center gap-3 p-2 rounded-xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/50">
            {isImage ? (
              <img
                src={previewUrl}
                alt="Receipt preview"
                className="h-14 w-14 rounded-lg object-cover border border-slate-200 dark:border-slate-700 shadow-sm"
              />
            ) : isPdf ? (
              <div className="h-14 w-14 flex flex-col items-center justify-center rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800 text-rose-500 shadow-sm font-black text-[11px] leading-tight">
                PDF
              </div>
            ) : (
              <div className="h-14 w-14 flex flex-col items-center justify-center rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800 text-primary-500 shadow-sm font-bold text-[10px] leading-tight">
                FILE
              </div>
            )}
            <div className="max-w-[200px] truncate">
              <span className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">
                {isFileObj ? value.name : "Attached File"}
              </span>
            </div>
            <div className="flex items-center gap-1.5 ml-2">
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="h-8 px-2.5 flex items-center justify-center rounded-lg bg-white dark:bg-slate-700 text-primary-600 shadow-sm hover:text-primary-700 transition-all border border-slate-200 dark:border-slate-600 text-xs font-bold"
              >
                View
              </a>
              <button
                type="button"
                onClick={() => onChange(null)}
                className="h-8 w-8 flex items-center justify-center rounded-lg bg-white dark:bg-slate-700 text-rose-500 shadow-sm hover:text-rose-600 transition-all border border-slate-200 dark:border-slate-600"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        ) : (
          <div className="relative group">
            <input
              type="file"
              accept=".pdf,image/*"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  onChange(e.target.files[0]);
                }
              }}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
            />
            <div className="flex items-center gap-3 px-4 py-2 rounded-lg border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50 group-hover:border-primary-500 transition-all">
              <div className="h-7 w-7 flex items-center justify-center rounded-md bg-white dark:bg-slate-700 shadow-sm text-slate-400 group-hover:text-primary-500 transition-colors border border-slate-100 dark:border-slate-600">
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
              </div>
              <span className="text-[11px] text-slate-500 dark:text-slate-400 font-bold uppercase tracking-widest">
                Choose File (PDF/Image)
              </span>
            </div>
          </div>
        )}
      </div>
      {error && (
        <p className="text-[10px] font-bold text-rose-500 uppercase tracking-tight mt-1">
          {error}
        </p>
      )}
    </div>
  );
};

function ExpenseOverheadFormPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useEncryptedParams();

  // --- States ---
  const [formData, setFormData] = useState({
    expenseCategoryId: "",
    overheadDetails: "",
    price: "",
    remark: "",
    file: null,
    isActive: true,
  });
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);
  const [formError, setFormError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const isEditPage = Boolean(id);
  const formMode = isEditPage ? "edit" : "create";

  const basePath = useMemo(() => {
    if (location.pathname.includes("/settings-master/expense-overhead"))
      return "/settings-master/expense-overhead";
    return "/expense-overhead";
  }, [location.pathname]);

  // --- Fetch Active Expense Categories ---
  const fetchCategories = async () => {
    setIsLoadingCategories(true);
    try {
      const categories = await expenseCategoryService.getActiveExpenseCategories();
      setExpenseCategories(categories || []);
      return categories || [];
    } catch (err) {
      console.error("Failed to load expense categories:", err);
      return [];
    } finally {
      setIsLoadingCategories(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // --- Master Modals Setup ---
  const { openModal, renderModals } = useMasterModals({
    expenseCategory: async (newCat) => {
      const updatedCategories = await fetchCategories();
      const newId = newCat?.expenseCategoryId || newCat?.id;
      if (newId) {
        setFormData((prev) => ({ ...prev, expenseCategoryId: String(newId) }));
        setFormErrors((prev) => ({ ...prev, expenseCategoryId: null }));
      }
    },
  });

  // --- Initial Load for Edit ---
  useEffect(() => {
    if (!isEditPage || !id) return;

    let isMounted = true;
    const loadEditData = async () => {
      setIsLoadingEdit(true);
      setFormError(null);
      try {
        const response = await apiClient.get(`/expense-overhead/${id}`);
        const payload = response?.data || {};
        const data = payload?.data || payload || {};
        if (!isMounted) return;

        const catId = data.expenseCategoryId || data.expenseCategory?.expenseCategoryId || "";
        setFormData({
          expenseCategoryId: catId ? String(catId) : "",
          overheadDetails: data.overheadDetails || "",
          price:
            data.price !== undefined && data.price !== null
              ? String(data.price)
              : "",
          remark: data.remark || "",
          file: data.file || data.fileUrl || null,
          isActive: data.isActive !== false,
        });

        if (data.expenseCategory && data.expenseCategory.expenseCategoryId) {
          setExpenseCategories((prev) => {
            const exists = prev.some(
              (c) => String(c.expenseCategoryId) === String(data.expenseCategory.expenseCategoryId)
            );
            if (!exists) {
              return [data.expenseCategory, ...prev];
            }
            return prev;
          });
        }
      } catch (err) {
        if (!isMounted) return;
        const normalized = normalizeApiError(err);
        setFormError(normalized.message);
      } finally {
        if (isMounted) setIsLoadingEdit(false);
      }
    };

    loadEditData();
    return () => {
      isMounted = false;
    };
  }, [id, isEditPage]);

  // --- Handlers ---
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let finalValue = type === "checkbox" ? checked : value;

    if (name === "price") {
      const numVal = parseFloat(value);
      if (!isNaN(numVal) && numVal < 0) {
        finalValue = "";
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: finalValue,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const errors = {};
    if (!formData.expenseCategoryId) {
      errors.expenseCategoryId = "Expense Category is required";
    }
    if (!formData.overheadDetails.trim()) {
      errors.overheadDetails = "Overhead Details are required";
    }
    if (
      !formData.price ||
      isNaN(formData.price) ||
      Number(formData.price) < 1
    ) {
      errors.price = "Price must be at least 1";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    if (!validate()) return;

    setIsSaving(true);
    setFormError(null);
    try {
      const payload = new FormData();
      if (formData.expenseCategoryId) {
        payload.append("expenseCategoryId", formData.expenseCategoryId);
      }
      payload.append("overheadDetails", formData.overheadDetails.trim());
      payload.append("price", formData.price);
      payload.append("remark", (formData.remark || "").trim());
      payload.append("isActive", String(formData.isActive));
      if (formData.file instanceof File) {
        payload.append("file", formData.file);
      }

      if (formMode === "edit" && id) {
        await apiClient.patch(`/expense-overhead/${id}`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        alertService.success("Expense Overhead updated successfully.");
      } else {
        await apiClient.post("/expense-overhead", payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        alertService.success("Expense Overhead created successfully.");
      }
      navigate(basePath);
    } catch (err) {
      const normalized = normalizeApiError(err);
      setFormError(normalized.message);
      alertService.error(normalized.message);
    } finally {
      setIsSaving(false);
    }
  };

  const notFound =
    formMode === "edit" &&
    !isLoadingEdit &&
    !formData.overheadDetails &&
    !formError;

  return (
    <div className="space-y-3 mx-auto">
      <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-4">
        <div>
          <h1 className="text-2xl font-black text-slate-800 dark:text-white uppercase tracking-tight">
            {formMode === "edit" ? "Edit" : "Add"}{" "}
            <span className="text-primary-600">Expense Overhead</span>
          </h1>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-1">
            Settings Master / Expense Overhead /{" "}
            {formMode === "edit" ? "Edit" : "New"}
          </p>
        </div>
        <Button variant="secondary" onClick={() => navigate(basePath)}>
          <svg
            className="mr-2 h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          Back to List
        </Button>
      </div>

      {formError ? <ErrorAlert message={formError} /> : null}

      {notFound ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:bg-rose-900/20 dark:border-rose-800">
          Record not found.
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900 overflow-hidden">
          <form onSubmit={handleSave}>
            <table className="w-full border-collapse">
              <tbody>
                {/* --- Expense Category --- */}
                <tr className="border-b border-slate-100 dark:border-slate-800">
                  <td className="w-1/3 py-4 px-6 text-[11px] font-black uppercase tracking-widest text-slate-500 bg-slate-50/50 dark:bg-slate-800/50">
                    Expense Category <span className="text-danger">*</span>
                  </td>
                  <td className="py-3 px-6">
                    <SearchableDropdown
                      name="expenseCategoryId"
                      value={formData.expenseCategoryId}
                      onChange={handleInputChange}
                      options={expenseCategories.map((cat) => ({
                        value: String(cat.expenseCategoryId),
                        label: cat.categoryName,
                      }))}
                      placeholder={
                        isLoadingCategories
                          ? "Loading categories..."
                          : "Search expense category"
                      }
                      error={formErrors.expenseCategoryId}
                      disabled={isLoadingCategories}
                      onAdd={() => openModal("expenseCategory")}
                      addLabel="ADD NEW"
                      buttonClassName="rounded-xl px-3 py-2 text-slate-800 font-medium border-slate-300 dark:border-slate-700 shadow-sm"
                    />
                  </td>
                </tr>

                {/* --- Overhead Details --- */}
                <tr className="border-b border-slate-100 dark:border-slate-800">
                  <td className="w-1/3 py-4 px-6 text-[11px] font-black uppercase tracking-widest text-slate-500 bg-slate-50/50 dark:bg-slate-800/50">
                    Overhead Details <span className="text-danger">*</span>
                  </td>
                  <td className="py-3 px-6">
                    <Input
                      name="overheadDetails"
                      value={formData.overheadDetails}
                      onChange={handleInputChange}
                      error={formErrors.overheadDetails}
                      placeholder="e.g. Sample Overhead Details"
                    />
                  </td>
                </tr>

                {/* --- Price --- */}
                <tr className="border-b border-slate-100 dark:border-slate-800">
                  <td className="w-1/3 py-4 px-6 text-[11px] font-black uppercase tracking-widest text-slate-500 bg-slate-50/50 dark:bg-slate-800/50">
                    Price <span className="text-danger">*</span>
                  </td>
                  <td className="py-3 px-6">
                    <div className="max-w-[240px]">
                      <Input
                        name="price"
                        type="number"
                        step="0.01"
                        min="1"
                        value={formData.price}
                        onChange={handleInputChange}
                        error={formErrors.price}
                        placeholder="e.g. 125.50"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "E") {
                            e.preventDefault();
                          }
                        }}
                      />
                    </div>
                  </td>
                </tr>

                {/* --- Remark --- */}
                <tr className="border-b border-slate-100 dark:border-slate-800">
                  <td className="w-1/3 py-4 px-6 text-[11px] font-black uppercase tracking-widest text-slate-500 bg-slate-50/50 dark:bg-slate-800/50">
                    Remark
                  </td>
                  <td className="py-3 px-6">
                    <TextArea
                      name="remark"
                      value={formData.remark}
                      onChange={handleInputChange}
                      error={formErrors.remark}
                      placeholder="Enter a remark..."
                      rows={3}
                    />
                  </td>
                </tr>

                {/* --- Attachment File --- */}
                <tr className="border-b border-slate-100 dark:border-slate-800">
                  <td className="w-1/3 py-4 px-6 text-[11px] font-black uppercase tracking-widest text-slate-500 bg-slate-50/50 dark:bg-slate-800/50">
                    Attachment File
                  </td>
                  <td className="py-3 px-6">
                    <FilePreviewField
                      label="File Upload"
                      value={formData.file}
                      error={formErrors.file}
                      onChange={(file) => {
                        setFormData((prev) => ({ ...prev, file }));
                        setFormErrors((prev) => ({ ...prev, file: null }));
                      }}
                    />
                  </td>
                </tr>

                {/* --- Status --- */}
                <tr>
                  <td className="w-1/3 py-4 px-6 text-[11px] font-black uppercase tracking-widest text-slate-500 bg-slate-50/50 dark:bg-slate-800/50">
                    Status
                  </td>
                  <td className="py-3 px-6">
                    <label className="flex cursor-pointer items-center gap-2">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                        className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-600/20"
                      />
                      <span className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase">
                        {formData.isActive ? "Active" : "Inactive"}
                      </span>
                    </label>
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="bg-slate-50 dark:bg-slate-800/30 p-6 flex items-end justify-end gap-3">
              
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate(basePath)}
                disabled={isSaving}
                className="px-6"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                isLoading={isSaving || isLoadingEdit}
                className="px-8 shadow-lg shadow-primary-500/20"
              >
                {formMode === "edit" ? "Update" : "Save"}
              </Button>
            </div>
          </form>
        </div>
      )}

      {renderModals()}
    </div>
  );
}

export default ExpenseOverheadFormPage;
